<template>
  <div class="container">
    <div class="paywall">
      <div class="columns">
        <section class="sidebar-column" id="payment-section-summary" aria-labelledby="section-summary-heading">
          <order-details-side :transaction="transaction"
                              :visibility="visibilityOptions"
                              :show-button="true" />
        </section>
        <div class="main-column">
          <div class="content">
            <section class="payment-fr-data">
              <div class="subpage-header">
                <h1 v-html="$t('paywall.frData.header')"></h1>
              </div>
              <p class="copy-data-text" v-html="$t('paywall.frData.info')"></p>
              <div v-if="canShowVerifyInfo" class="verify-info" v-html="$t('paywall.frData.verifyInfo', {backToShopText: $t('orderDetails.linkBack')})"></div>
              <div class="fr-info-box">
                <p>
                  <strong>{{ $t('paywall.frInfo.frData.text1') }}</strong>
                  {{ $t('paywall.frInfo.frData.text2') }}
                </p>
              </div>
              <div class="rows-wrapper">
                <div class="row">
                  <div class="col">
                    <p class="label">{{ $t('paywall.frData.timeLabel') }}</p>
                    <p class="value">
                      <span>{{ paymentData.transactionValidityTime | date }}</span>
                    </p>
                  </div>
                </div>
                <div class="row">
                  <div class="col">
                    <p class="label">{{ $t('paywall.frData.nrbLabel') }}</p>
                    <p class="value">
                      <span>{{ paymentFrData.nrb.value }}</span>
                      <button class="btn btn-plain btn-copy" @click="copy(paymentFrData.nrb.value, $event)" :title="$t('paywall.frData.linkCopy')"></button>
                    </p>
                  </div>
                </div>
                <div class="row">
                  <div class="col">
                    <p class="label">{{ $t('paywall.frData.nameLabel') }}</p>
                    <p class="value">
                      <span>{{ paymentFrData.name.value }}</span>
                      <button class="btn btn-plain btn-copy" @click="copy(paymentFrData.name.value, $event)" :title="$t('paywall.frData.linkCopy')"></button>
                    </p>
                  </div>
                </div>
                <div class="row">
                  <div class="col">
                    <p class="label">{{ $t('paywall.frData.addressLabel') }}</p>
                    <p class="value">
                      <span>{{ paymentFrData.address.value }}</span>
                      <button class="btn btn-plain btn-copy" @click="copy(paymentFrData.address.value, $event)" :title="$t('paywall.frData.linkCopy')"></button>
                    </p>
                  </div>
                </div>
                <div class="row">
                  <div class="col">
                    <p class="label">{{ $t('paywall.frData.titleLabel') }}</p>
                    <p class="value">
                      <span>{{ paymentFrData.title.value }}</span>
                      <button class="btn btn-plain btn-copy" @click="copy(paymentFrData.title.value, $event)" :title="$t('paywall.frData.linkCopy')"></button>
                    </p>
                  </div>
                </div>
                <div class="row">
                  <div class="col">
                    <p class="label">{{ $t('paywall.frData.amountLabel') }}</p>
                    <p class="value">
                      <span>{{ paymentFrData.amount.value | amount }} {{ paymentFrData.currency.value }}</span>
                      <button class="btn btn-plain btn-copy" @click="copy(paymentFrData.amount.value, $event)" :title="$t('paywall.frData.linkCopy')"></button>
                    </p>
                  </div>
                </div>
                <div class="row">
                  <div class="col">
                    <p class="label">SWIFT</p>
                    <p class="value">
                      <span>{{ paymentFrData.bic.value }}</span>
                      <button class="btn btn-plain btn-copy" @click="copy(paymentFrData.bic.value, $event)" :title="$t('paywall.frData.linkCopy')"></button>
                    </p>
                  </div>
                </div>
              </div>
              <div class="summary">
                <a :href="getPdfUrl(paymentData.additionalData.pdfHash)"
                   :title="$t('paywall.frData.linkGetPdfTitle')"
                   target="_blank"
                   class="btn btn-plain btn-download">
                  {{ $t('paywall.frData.linkGetPdf') }}
                </a>
                <div v-if="canShowLoginButton">
                  <a :href="paymentFrData.url.value"
                     target="_blank"
                     rel="noreferrer"
                     class="btn button-go-to-bank">
                    {{ $t('paywall.frData.linkGoToBank') }}
                  </a>
                </div>
              </div>
              <div class="row">
                <div class="change-payment-wrapper" v-if="showReturnToPaywaysButton">
                  <p>{{ $t('paywall.form.changePaywayLink1') }} <a href="" role="button" @click.prevent="setPaywallModeDefault">{{ $t('paywall.form.changePaywayLink2') }}</a></p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import OrderDetailsSide from '@/base/components/OrderDetails/Side'
import { MESSAGES } from '@/base/const/texts.const'
import { clipboardClass } from '@/base/class/clipboard.class'
import { MobileAppClass } from '@/base/class/mobileApp.class'
import { MOBILE_STATUS } from '@/base/const/mobileEvents.const'
import { pageTitle } from '@/base/const/routing.const'
import { ENDPOINTS } from '@/base/const/endpoints.const'
import { ToolsClass } from '@/base/class/tools.class'

export default {
  name: 'PaymentFrData',
  metaInfo () {
    return {
      title: pageTitle().base,
      titleTemplate: '%s | ' + pageTitle().start + ' - ' + pageTitle().frData
    }
  },
  data () {
    return {
      pdfInProgress: false
    }
  },
  components: {
    OrderDetailsSide
  },
  computed: {
    ...mapState({
      apiToken: state => state.payment.apiToken,
      paywayId: state => state.payment.form.paywayID,
      serviceId: state => state.payment.paymentData.serviceID,
      paymentFrData: state => state.payment.paymentData.frData,
      paymentData: state => state.payment.paymentData,
      transaction: state => state.payment.paymentData.transaction,
      visibilityOptions: state => state.payment.paymentData.visibilityOptions,
      showReturnToPaywaysButton: state => state.payment.paymentData.visibilityOptions
        ? state.payment.paymentData.visibilityOptions.showReturnToPaywaysButton
        : null
    }),
    canShowVerifyInfo: function () {
      const verifySidList = ToolsClass.isProduction() ? [190096] : [1009924]
      return verifySidList.includes(this.serviceId)
    },
    canShowLoginButton: function () {
      return !!this.paymentFrData.url.value &&
        this.paymentFrData.url.value.length > 0 &&
        this.paymentFrData.showBankUrlOnFront
    }
  },
  methods: {
    ...mapActions([
      'addMessageSuccess',
      'addMessageError'
    ]),
    copy (value, event) {
      setTimeout(() => {
        if (event.target) {
          event.target.focus()
        }
      })

      if (!value) {
        return
      }

      clipboardClass.copyToClipboard(value).then(() => {
        const message = {
          text: MESSAGES().success.copy,
          options: {
            time: 2000
          }
        }
        this.addMessageSuccess(message)
      }).catch(() => {
        const message = {
          text: MESSAGES().errors.copy
        }
        this.addMessageError(message)
      })
    },
    getPdfUrl (hash) {
      return `${ENDPOINTS.getPdf}?hash=${hash}`
    },
    setPaywallModeDefault () {
      location.reload()
    }
  },
  mounted () {
    MobileAppClass.postMessageSuccess(MOBILE_STATUS().paywayLoaded)
    MobileAppClass.supportExternalLinks()
  }
}
</script>

<style lang="scss" scoped>
@import "./src/base/assets/scss/components/PaywallForm.scss";
</style>
